import type { NavigationDestination } from '~/config/navigationSearchIndex';

export type NavigationMatch = {
  destination: NavigationDestination;
  label: string;
  group?: string;
  score: number;
};

const EXACT_SCORE = 100;
const PREFIX_SCORE = 80;
const WORD_PREFIX_SCORE = 60;
const CONTAINS_SCORE = 40;
const KEYWORD_SCORE = 30;
const GROUP_SCORE = 10;

const SOURCE_PRIORITY: Record<NavigationDestination['source'], number> = {
  menu: 2,
  report: 1,
  settings: 0,
};

const normalize = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, ' ');

const scoreText = (text: string, term: string): number => {
  const value = normalize(text);
  if (!value) return 0;
  if (value === term) return EXACT_SCORE;
  if (value.startsWith(term)) return PREFIX_SCORE;
  if (value.split(' ').some((word) => word.startsWith(term))) return WORD_PREFIX_SCORE;
  if (value.includes(term)) return CONTAINS_SCORE;
  return 0;
};

const scoreDestination = (destination: NavigationDestination, label: string, group: string | undefined, term: string): number => {
  const labelScore = scoreText(label, term);
  const rawKeywordScore = destination.keywords.reduce((best, keyword) => Math.max(best, scoreText(keyword, term)), 0);
  const keywordScore = rawKeywordScore === EXACT_SCORE ? EXACT_SCORE : Math.min(rawKeywordScore, KEYWORD_SCORE);
  const best = Math.max(labelScore, keywordScore);
  if (best > 0) return best;

  const combinedScore = group ? scoreText(`${group} ${label}`, term) : 0;
  if (combinedScore > 0) return GROUP_SCORE;

  return 0;
};

export const matchNavigationDestinations = (
  destinations: NavigationDestination[],
  searchTerm: string,
  translate: (key: string) => string,
  limit: number
): NavigationMatch[] => {
  const term = normalize(searchTerm);
  if (!term) return [];

  return destinations
    .map((destination) => {
      const label = translate(destination.labelKey);
      const group = destination.groupKey ? translate(destination.groupKey) : undefined;
      return { destination, label, group, score: scoreDestination(destination, label, group, term) };
    })
    .filter((match) => match.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        SOURCE_PRIORITY[b.destination.source] - SOURCE_PRIORITY[a.destination.source] ||
        a.label.length - b.label.length ||
        a.label.localeCompare(b.label)
    )
    .slice(0, limit);
};
